import { trackError } from '@billy/track-error'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { deleteFecImport, type UploadedFecImport, uploadFecFile } from '../query-api'
import { isFecImportConflictError } from '../utils/isFecImportConflictError'

interface StartUploadOptions {
  onFileUploaded?: (file: File) => void
  onImportCreated?: (importId: string) => void
}

interface PendingBatchRollback {
  importIds: string[]
  organizationId: string
}

const FILE_UPLOAD_PROGRESS_PERCENT = 40

const setFileProgressValue = (file: File, progressPercent: number) => (currentProgress: Map<File, number>) => {
  const nextProgress = new Map(currentProgress)
  nextProgress.set(file, progressPercent)

  return nextProgress
}

const getUploadedImportIds = (response: UploadedFecImport[] | undefined): string[] => {
  const importIds = (response ?? []).map((uploaded) => uploaded.id).filter((importId) => importId.length > 0)

  if (importIds.length === 0) {
    throw new Error('FEC import upload returned no import id')
  }

  return importIds
}

const registerCreatedImports = (
  importIds: string[],
  pendingBatchRollback: PendingBatchRollback | null,
  onImportCreated?: (importId: string) => void,
) => {
  for (const importId of importIds) {
    pendingBatchRollback?.importIds.push(importId)
    onImportCreated?.(importId)
  }
}

const reportUnexpectedUploadError = (error: unknown, isAborted: boolean) => {
  if (isAborted || isFecImportConflictError(error)) {
    return
  }

  const message = error instanceof Error ? error.message : String(error)
  trackError(message.startsWith('FEC import') ? error : new Error(`FEC import upload failed: ${message}`))
}

export const useFecImportUpload = () => {
  const [fileProgress, setFileProgress] = useState<Map<File, number>>(() => new Map())
  const [isUploading, setIsUploading] = useState(false)
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()
  const abortControllerRef = useRef<AbortController>()
  const pendingBatchRollbackRef = useRef<PendingBatchRollback | null>(null)

  useEffect(
    () => () => {
      abortControllerRef.current?.abort()

      const pendingBatchRollback = pendingBatchRollbackRef.current

      if (pendingBatchRollback) {
        for (const importId of pendingBatchRollback.importIds) {
          deleteFecImport(pendingBatchRollback.organizationId, importId).catch((error: unknown) => {
            trackError(
              new Error(
                `FEC import upload rollback failed: ${importId}: ${
                  error instanceof Error ? error.message : String(error)
                }`,
              ),
            )
          })
        }
      }
    },
    [],
  )

  const startUpload = useCallback(
    async (files: File[], options?: StartUploadOptions) => {
      if (files.length === 0) {
        return
      }

      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('FEC import upload failed: missing organization id')
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController
      pendingBatchRollbackRef.current = { importIds: [], organizationId }
      setFileProgress(new Map(files.map((file) => [file, 0])))
      setIsUploading(true)

      try {
        for (const file of files) {
          setFileProgress(setFileProgressValue(file, FILE_UPLOAD_PROGRESS_PERCENT))
          const importIds = getUploadedImportIds(await uploadFecFile(organizationId, file, abortController.signal))
          setFileProgress(setFileProgressValue(file, 100))
          registerCreatedImports(importIds, pendingBatchRollbackRef.current, options?.onImportCreated)
          options?.onFileUploaded?.(file)
        }
      } catch (error) {
        setFileProgress(new Map())
        setIsUploading(false)
        reportUnexpectedUploadError(error, abortController.signal.aborted)
        throw error
      } finally {
        pendingBatchRollbackRef.current = null
      }

      await queryClient.invalidateQueries([QueryKeys.FecImports, organizationId])
    },
    [organization?.id, queryClient],
  )

  return { fileProgress, isUploading, startUpload }
}
